import { useEffect, useMemo, useRef, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact, resolveCanvasColor } from "../../lib/renderers.js";
import { layoutClusterIcicle, ancestorChain } from "../../lib/clusterIcicle.js";
import { observeSettledSize } from "../../lib/settledResize.js";
import { useThemeVersion } from "../../lib/theme.js";

// HierarchyMap — the whole of a tree drawn as an icicle map, with one node
// pinpointed inside it. Extracted from the cluster Detail lens so any
// hierarchical dimension (clusters today; memories / files could follow) can
// render its taxonomy the same way.
//
//   • Geometry is structure — spans ∝ leaf count, so the map is the same atlas
//     at every scope and the eye can learn it.
//   • Heat is the scope — band intensity is the scoped activity rolled up the
//     hierarchy, showing where this scope's traffic lives in the taxonomy.
//   • The selected node pins green with a chart-style crosshair through the
//     full depth (visible even when its band is a sliver), and its ancestor
//     path lights up above it.
//
// Every band navigates: clicking calls onSelect(id). Callers own what that
// means (re-rooting a scope step, etc.). Pure presentation otherwise — takes a
// tree + a scoped-activity map, holds no data of its own.

const ROW_H = 30;
const ROW_GAP = 2;
const LABEL_MIN_W = 46;
const CARD_MAX_W = 220;

export function MapSkeleton() {
  return (
    <div className="grid gap-[2px] p-[10px]">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="h-[28px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
      ))}
    </div>
  );
}

// The band layer, drawn to CANVAS. The SVG version held ~3.5k DOM nodes at
// leiden scale (1.1k bands × rect/text/g): the browser re-rasterized the whole
// vector tree every frame of a resize drag, and each settled relayout re-diffed
// it through React. One canvas paint is ~2ms, the texture stretches for free
// mid-drag (the same contract as every chart surface), and React's tree shrinks
// to one element. Hover/crosshair/tooltip ride above as three overlay divs
// positioned in percent-x (so they stay true even mid-stretch).
function drawBandLayer(canvas, layout, selectedId, pathIds, wCss, hueOf) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(wCss * dpr));
  canvas.height = Math.max(1, Math.round(layout.height * dpr));
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const styles = getComputedStyle(document.documentElement);
  const green = resolveCanvasColor("var(--green)", styles) || "#7fa16c";
  const lineSoft = resolveCanvasColor("var(--line-soft)", styles) || "#333";
  const ink = resolveCanvasColor("var(--ink)", styles) || "#eee";
  const muted = resolveCanvasColor("var(--muted)", styles) || "#999";

  ctx.clearRect(0, 0, wCss, layout.height);
  ctx.textBaseline = "middle";
  const heat = (act) => (layout.maxActivity > 0 ? Math.sqrt(act / layout.maxActivity) : 0);

  for (const b of layout.bands) {
    const isSelected = b.id === selectedId;
    const onPath = pathIds.has(b.id);
    const t = heat(b.activity);
    // A caller-supplied hue names what a band belongs to (a group, say); heat
    // still sets its strength, and selection stays the green ring below.
    const hue = hueOf ? hueOf(b.id) : null;
    if (hue) {
      ctx.globalAlpha = isSelected ? 0.95 : onPath ? Math.max(0.55, 0.3 + 0.5 * t) : 0.22 + 0.5 * t;
      ctx.fillStyle = hue;
    } else if (hueOf) {
      // A hued map's uncoloured bands (its root, say) stay a wash even when
      // selected: the ring and crosshair carry the selection, and a full green
      // fill would sink the label in the dark theme.
      ctx.globalAlpha = isSelected ? 0.34 : onPath ? 0.24 : 0.14;
      ctx.fillStyle = isSelected || onPath ? green : lineSoft;
    } else {
      ctx.globalAlpha = isSelected
        ? 0.92
        : onPath
          ? Math.max(0.4, 0.18 + 0.55 * t)
          : b.activity > 0
            ? 0.12 + 0.55 * t
            : 0.3;
      ctx.fillStyle = isSelected || b.activity > 0 ? green : lineSoft;
    }
    // The 1px right inset is the band separator — the plot-bg panel shows
    // through, same read the SVG strokes gave.
    ctx.fillRect(b.x, b.y, Math.max(1, b.w - 1), b.h);
    if (isSelected) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = green;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(b.x + 0.75, b.y + 0.75, Math.max(1, b.w - 1) - 1.5, b.h - 1.5);
    }
    if (b.w >= LABEL_MIN_W) {
      const labelChars = Math.max(2, Math.floor((b.w - 12) / 6));
      const label = b.name.length > labelChars ? `${b.name.slice(0, Math.max(1, labelChars - 1))}…` : b.name;
      ctx.globalAlpha = 1;
      ctx.fillStyle = isSelected || onPath || hue ? ink : muted;
      ctx.font = `${isSelected ? 500 : 400} 10px Inter, system-ui, sans-serif`;
      ctx.fillText(label, b.x + 6, b.y + b.h / 2);
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * @param {object}   props
 * @param {object}   props.tree        {byId, childrenOf, roots} (loadClusterTree shape)
 * @param {Map}      props.activity    scoped activity rolled up the tree (rollupActivityMap)
 * @param {string}   props.selectedId  the pinned node
 * @param {Function} props.onSelect    (id) => void, called on a band click
 * @param {string}   [props.ariaLabel]
 * @param {string}   [props.emptyLabel]
 * @param {string}   [props.unit]      tooltip activity noun (singular), e.g. "prompt"
 * @param {Function} [props.shareOf]   (id) => share of the parent's span, for trees not sized by leaf count
 * @param {Function} [props.hueOf]     (id) => a band colour, or null for the heat green
 * @param {Function} [props.describe]  (band) => { title, detail } for the hover card
 */
export default function HierarchyMap({
  tree,
  activity,
  selectedId,
  onSelect,
  ariaLabel = "Hierarchy map. Select a band to navigate.",
  emptyLabel = "Nothing to map yet.",
  unit = "prompt",
  shareOf = null,
  hueOf = null,
  describe = null,
}) {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const [width, setWidth] = useState(0);
  // Hover = the hit band + the cursor position (for the tooltip); null when the
  // pointer is in a gap or outside.
  const [hover, setHover] = useState(null);
  useEffect(() => {
    const node = hostRef.current;
    if (!node) return undefined;
    const measure = () => setWidth(Math.max(320, Math.round(node.getBoundingClientRect().width)));
    measure();
    return observeSettledSize(node, measure);
  }, []);

  const w = width || 760;
  const layout = useMemo(
    () => layoutClusterIcicle(tree, { width: w, rowHeight: ROW_H, rowGap: ROW_GAP, activity, shareOf }),
    [tree, w, activity, shareOf],
  );
  const pathIds = useMemo(
    () => new Set(ancestorChain(tree, selectedId).map((n) => n.id)),
    [tree, selectedId],
  );
  // Bands bucketed per depth row and x-sorted, for O(log n) hit-testing from the
  // single pointer handler.
  const bandsByDepth = useMemo(() => {
    const rows = [];
    for (const b of layout.bands) (rows[b.depth] ??= []).push(b);
    for (const row of rows) row?.sort((a, b) => a.x - b.x);
    return rows;
  }, [layout]);
  const selectedBand = layout.bands.find((b) => b.id === selectedId) ?? null;

  // One canvas paint per (layout, selection, theme) — hover never repaints; a
  // resize repaints once on settle via the layout memo.
  const themeVersion = useThemeVersion();
  useEffect(() => {
    if (canvasRef.current && layout.bands.length) {
      drawBandLayer(canvasRef.current, layout, selectedId, pathIds, w, hueOf);
    }
  }, [layout, selectedId, pathIds, w, themeVersion, hueOf]);

  if (!layout.bands.length) {
    return <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>{emptyLabel}</span>;
  }

  // Pointer position → band, in viewBox coordinates.
  const hitTest = (evt) => {
    const el = canvasRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const sx = ((evt.clientX - r.left) / r.width) * w;
    const sy = ((evt.clientY - r.top) / r.height) * layout.height;
    const depth = Math.floor(sy / (ROW_H + ROW_GAP));
    if (sy - depth * (ROW_H + ROW_GAP) > ROW_H) return null; // in the row gap
    const row = bandsByDepth[depth];
    if (!row) return null;
    let lo = 0;
    let hi = row.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const b = row[mid];
      if (sx < b.x) hi = mid - 1;
      else if (sx >= b.x + b.w) lo = mid + 1;
      else return b;
    }
    return null;
  };

  const onPointerMove = (evt) => {
    const b = hitTest(evt);
    const r = hostRef.current?.getBoundingClientRect();
    if (!b || !r) {
      if (hover) setHover(null);
      return;
    }
    setHover({ band: b, cx: evt.clientX - r.left, cy: evt.clientY - r.top });
  };

  const hoverBand = hover?.band ?? null;
  const hoverCard = hoverBand && describe ? describe(hoverBand) : null;

  return (
    <div
      ref={hostRef}
      className="relative min-w-0 overflow-hidden"
      style={{ cursor: hoverBand && hoverBand.id !== selectedId ? "pointer" : "default" }}
      onPointerMove={onPointerMove}
      onPointerLeave={() => setHover(null)}
      onClick={(evt) => {
        const b = hitTest(evt);
        if (b && b.id !== selectedId) onSelect?.(b.id);
      }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        style={{ display: "block", width: "100%", height: `${layout.height}px` }}
      />
      {/* Hover overlay — one outline div over the hit band. Percent-x so it
          tracks the canvas even while a drag stretches it. */}
      {hoverBand && hoverBand.id !== selectedId ? (
        <div
          className="pointer-events-none absolute border-[1.2px] border-[color:var(--green)]"
          style={{
            left: `${(hoverBand.x / w) * 100}%`,
            width: `${(Math.max(1, hoverBand.w - 1) / w) * 100}%`,
            top: hoverBand.y,
            height: hoverBand.h,
          }}
        />
      ) : null}
      {/* The pinpoint — a chart-style dashed crosshair through the selected
          band's center across the full depth, so even a sliver-wide node is
          findable at a glance. */}
      {selectedBand ? (
        <div
          className="pointer-events-none absolute inset-y-0 border-l border-dashed border-[color:var(--green)]"
          style={{ left: `${((selectedBand.x + selectedBand.w / 2) / w) * 100}%`, opacity: 0.6 }}
        />
      ) : null}
      {/* Chart-style hover tooltip (native <title> can't ride a single delegated
          handler, and this is faster + consistent with the activity surface's
          hover box anyway). */}
      {/* The card sits right of the pointer, or left of it where it would run past the map's edge: the map clips
          what overflows it. */}
      {hoverBand ? (
        <div
          className="pointer-events-none absolute z-10 border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[5px] text-[10px] leading-[1.35] shadow-lg"
          style={{
            ...(hover.cx + 12 + CARD_MAX_W > w ? { right: Math.max(0, w - hover.cx + 12) } : { left: Math.max(0, hover.cx + 12) }),
            top: Math.max(0, hover.cy - 40),
            maxWidth: CARD_MAX_W,
          }}
        >
          <div className="truncate font-medium text-[color:var(--ink)]">{hoverCard?.title ?? hoverBand.name}</div>
          <div className="text-[color:var(--muted)]">
            {hoverCard ? hoverCard.detail : (
              <>
                {formatCompact(hoverBand.activity)} {unit}{hoverBand.activity === 1 ? "" : "s"} in scope ·{" "}
                {hoverBand.leafCount} {hoverBand.leafCount === 1 ? "leaf" : "leaves"}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
